import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm, useWatch } from 'react-hook-form';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons, ImageField } from '@barboxx/admin-components';
import { STANDARD_DRINK_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { StandardDrink } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import {
    ImageKitCustomMetadataKeys,
    ImageKitMenuItemFolders,
    ImageKitNamedTransforms,
    ImageKitTypes,
} from '@barboxx/types';

import { REACT_APP_ENV } from '../../../../../environments/environment';

interface Props {
    standardDrink: StandardDrink;
}

export const StandardDrinkDetailsCard = ({ standardDrink }: Props) => {
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const mutateUser = useMutation({
        mutationKey: ['standardDrink', standardDrink.id],
        mutationFn: (data: StandardDrink.UpdateRequest) =>
            Api.StandardDrinks.StandardDrink.putStandardDrink(
                standardDrink.id,
                data
            ),
    });

    const formContext = useForm<StandardDrink.UpdateRequest>({
        resolver: yupResolver(STANDARD_DRINK_SCHEMA),
        defaultValues: standardDrink,
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: StandardDrink.UpdateRequest) => {
        mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated standard drink!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating standard drink!', {
                    severity: 'error',
                });
            });
    };

    const values = useWatch(formContext) as StandardDrink.DetailsResponse;

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="StandardDrink Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <TextFieldElement
                            label="Preparation Instructions"
                            name="preparationInstructions"
                            fullWidth
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="Is Alcoholic"
                            name="isAlcoholic"
                            disabled={!editable}
                        />
                        <CheckboxElement
                            label="Is Shot"
                            name="isShot"
                            disabled={!editable}
                        />
                        {/* <IngredientsInput /> */}

                        <ImageField
                            label="Image"
                            name="image"
                            readOnly={!editable}
                            imagekitUploadProps={{
                                width: 200,
                                fileName: `${values.id}`,
                                folder: ImageKitMenuItemFolders.StandardDrink,
                                env: REACT_APP_ENV,
                                customMetadata: {
                                    [ImageKitCustomMetadataKeys.Id]: `${values['id']}`,
                                    [ImageKitCustomMetadataKeys.Type]:
                                        ImageKitTypes.MenuItem,
                                    [ImageKitCustomMetadataKeys.Env]:
                                        REACT_APP_ENV,
                                },
                                tags: [
                                    values['name'],
                                    ImageKitTypes.MenuItem,
                                    `${values['id']}`,
                                    REACT_APP_ENV,
                                ],
                            }}
                            imagekitProps={{
                                width: 300,
                                transformation: [
                                    {
                                        raw: ImageKitNamedTransforms.Web.cover,
                                    },
                                ],
                                loading: 'lazy',
                                lqip: {
                                    raw: ImageKitNamedTransforms.Web.coverLq,
                                },
                            }}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="StandardDrink">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
